import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Single-flight token refresh.
 *
 * Every request that gets a 401 used to POST its own /auth/refresh-token. The
 * dashboard alone runs twelve queries, so one access-token expiry produced
 * twelve concurrent refreshes — and because the server rotates the refresh
 * token and revokes the old one, the first refresh invalidated the token the
 * other eleven were still presenting. Those eleven got a 401 back and each
 * one ran `logout()` + a hard redirect, so a routine expiry threw the operator
 * out of the system and made them log in again (which then costs an OTP, and
 * OTP sends are limited far more tightly than API calls).
 *
 * Now the first 401 starts the refresh and every other caller awaits that same
 * promise: one refresh, one rotation, no spurious logout.
 */
let refreshInFlight: Promise<string> | null = null;

const refreshAccessToken = (refreshToken: string): Promise<string> => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = axios
    .post(`${API_BASE_URL}/auth/refresh-token`, { refreshToken })
    .then((response) => {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data;
      const { user, setAuth } = useAuthStore.getState();
      if (!user) throw new Error('no user in store');
      setAuth(user, accessToken, newRefreshToken);
      return accessToken as string;
    })
    .finally(() => { refreshInFlight = null; });

  return refreshInFlight;
};

// Track when we last showed a toast for 429 so a wave of rate-limited
// requests doesn't spam the user with a dozen identical toasts.
let last429ToastAt = 0;

// Response interceptor for error handling, token refresh, and 429 retry.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // 429 is NOT retried.
    //
    // The previous interceptor waited out Retry-After and re-sent the request
    // up to twice. That turns one blocked call into three and keeps the bucket
    // empty, so a brief burst became a self-sustaining storm — which is what
    // produced the repeating "server busy, retrying in 15s" toast. React Query
    // already refetches on its own schedule, so dropping the call simply means
    // the next scheduled poll picks the data up once the window resets.
    if (error.response?.status === 429) {
      const retryAfterSec = Math.min(
        parseInt((error.response.headers as any)?.['retry-after'], 10) || 5, 60,
      );
      // One toast per window rather than one per blocked request.
      const now = Date.now();
      if (now - last429ToastAt > retryAfterSec * 1000) {
        last429ToastAt = now;
        try {
          const { toast } = await import('react-hot-toast');
          toast.error('الخادم مشغول مؤقتاً، سيتم التحديث تلقائياً', {
            duration: 4000, id: 'rate-limit',
          });
        } catch { /* toast optional */ }
      }
      return Promise.reject(error);
    }

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const { refreshToken, logout } = useAuthStore.getState();

      if (refreshToken) {
        try {
          const newAccessToken = await refreshAccessToken(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          logout();
          window.location.href = '/login';
        }
      } else {
        logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  // deviceToken lets the server skip the OTP step when this browser was
  // previously marked trusted. Omitted when there is none stored.
  login: (email: string, password: string, deviceToken?: string) =>
    api.post('/auth/login', { email, password, ...(deviceToken ? { deviceToken } : {}) }),

  // OTP step 2 — server dispatches a code to the chosen channel.
  sendOtp: (loginSessionToken: string, channel: 'sms' | 'email') =>
    api.post('/auth/otp/send', { loginSessionToken, channel }),

  // Final step — exchanges (sessionToken + code) for the real JWT pair.
  verifyOtp: (loginSessionToken: string, code: string, trustDevice: boolean = false) =>
    api.post('/auth/otp/verify', { loginSessionToken, code, trustDevice }),

  // Redeems the one-shot token /login returns when must_change_password is
  // set. Unauthenticated by design — the user has no session yet.
  forcePasswordChange: (passwordChangeToken: string, oldPassword: string, newPassword: string) =>
    api.post('/auth/force-password-change', { passwordChangeToken, oldPassword, newPassword }),

  register: (data: {
    email: string;
    password: string;
    fullName: string;
    phone?: string;
    role?: string;
    organizationId?: string;
  }) => api.post('/auth/register', data),
  
  getProfile: () => api.get('/auth/profile'),
  
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { oldPassword, newPassword }),
  
  logout: () => api.post('/auth/logout'),

  // Active login sessions for the current user.
  listSessions: () => api.get('/auth/sessions'),

  // End one specific session (the user picks it from the list).
  revokeSession: (jti: string) => api.delete(`/auth/sessions/${jti}`),

  // Kick every OTHER session — keeps the current one alive so the
  // user isn't logged out immediately.
  revokeOtherSessions: () => api.delete('/auth/sessions'),

  // Forget every trusted device, so each one must pass OTP again.
  revokeTrustedDevices: () => api.post('/auth/trusted-devices/revoke-all'),
};

// Vehicles API
export const vehiclesApi = {
  getAll: (params?: { organizationId?: string; status?: string; isActive?: boolean }) =>
    api.get('/vehicles', { params }),
  
  getWithPositions: (organizationId?: string) =>
    api.get('/vehicles/with-positions', { params: { organizationId } }),
  
  getById: (id: string) => api.get(`/vehicles/${id}`),
  
  create: (data: {
    plateNumber: string;
    vehicleType: string;
    model?: string;
    year?: number;
    chassisNumber?: string;
    simNumber?: string;
    organizationId?: string;
    deviceImei?: string;
    deviceModel?: string;
    // Real dashboard reading in km. Recording it pins the tracker's own
    // counter as the baseline, so later totals are derived from the delta.
    initialOdometerKm?: number | null;
  }) => api.post('/vehicles', data),

  update: (id: string, data: Partial<{
    plateNumber: string;
    vehicleType: string;
    model?: string;
    year?: number;
    chassisNumber?: string;
    simNumber?: string;
    organizationId?: string;
    deviceImei?: string;
    deviceModel?: string;
    // Real dashboard reading in km. Recording it pins the tracker's own
    // counter as the baseline, so later totals are derived from the delta.
    initialOdometerKm?: number | null;
  }>) => api.put(`/vehicles/${id}`, data),
  
  updateStatus: (id: string, status: string) =>
    api.patch(`/vehicles/${id}/status`, { status }),
  
  delete: (id: string) => api.delete(`/vehicles/${id}`),
  
  getLatestPosition: (id: string) => api.get(`/vehicles/${id}/position`),
  
  getPositionHistory: (id: string, startTime: string, endTime: string) =>
    api.get(`/vehicles/${id}/history`, { params: { startTime, endTime } }),

  // Travelled path for a window, shaped for review and playback: lean points,
  // derived stops, and a summary. Distinct from getPositionHistory, which
  // returns whole position rows and is too heavy to animate.
  getTrack: (id: string, startTime: string, endTime: string) =>
    api.get(`/vehicles/${id}/track`, { params: { startTime, endTime } }),

  // I/O elements (real-time + historical) — needed by VehicleDetail.
  // Recorded reading plus everything the tracker counted since.
  getOdometer: (id: string) => api.get(`/vehicles/${id}/odometer`),

  getLatestIo: (id: string) => api.get(`/vehicles/${id}/io/latest`),

  getIoHistory: (id: string, metric: string, hours: number = 24) =>
    api.get(`/vehicles/${id}/io/history`, { params: { metric, hours } }),
};

// Organizations API
export const organizationsApi = {
  getAll: (params?: { type?: string; parentId?: string }) =>
    api.get('/organizations', { params }),
  
  getById: (id: string) => api.get(`/organizations/${id}`),

  // Coordinates are required: they are the centre point the operating-zone
  // check measures from. geofenceRadiusKm omitted = use the system default.
  create: (data: {
    // Optional: the facility directory is Arabic-only and every screen falls
    // back to the Arabic name.
    name?: string;
    nameAr: string;
    type: string;
    /** Holds the city chosen from the directory. */
    address?: string;
    phone?: string;
    email?: string;
    latitude: string;
    longitude: string;
    geofenceRadiusKm?: string | number | null;
    parentId?: string;
    /** Set when imported from the health-facility directory. */
    externalFacilityId?: number | null;
  }) => api.post('/organizations', data),

  update: (id: string, data: Partial<{
    name?: string;
    nameAr: string;
    type: string;
    address?: string;
    phone?: string;
    email?: string;
    latitude: string;
    longitude: string;
    geofenceRadiusKm?: string | number | null;
    externalFacilityId?: number | null;
  }>) => api.put(`/organizations/${id}`, data),
  
  delete: (id: string) => api.delete(`/organizations/${id}`),
};

/** One facility from the external health-facility directory, via our proxy. */
export interface FacilityOption {
  id: number;
  name: string;
  type: string | null;
  city: string | null;
  icon: string | null;
  /** Strings, matching the coordinate fields everywhere else in the app. */
  latitude: string | null;
  longitude: string | null;
  /** Our own org type this maps to, or null when the upstream type is unknown. */
  suggestedType: 'ministry' | 'hospital' | 'department' | null;
}

export interface CityOption {
  id: number | null;
  name: string;
  facilityCount: number | null;
}

/**
 * Proxy over the public health-facility directory. Goes through our backend
 * rather than the browser: the upstream sends no CORS headers, and proxying
 * lets one cached sweep serve every operator.
 */
export const healthFacilitiesApi = {
  search: (params: { q?: string; limit?: number }) =>
    api.get('/health-facilities', { params }),

  cities: () => api.get('/health-facilities/cities'),
};

// Users API — admin-driven CRUD. All endpoints require ministry_admin
// or higher; delete additionally requires super_admin (enforced server
// side).
export const usersApi = {
  getAll: (params?: { search?: string; role?: string; organizationId?: string; isActive?: boolean }) =>
    api.get('/users', { params }),

  getById: (id: string) => api.get(`/users/${id}`),

  create: (data: {
    email: string;
    password: string;
    fullName: string;
    phone?: string;
    role?: string;
    organizationId?: string;
    isActive?: boolean;
    mustChangePassword?: boolean;
  }) => api.post('/users', data),

  update: (id: string, data: Partial<{
    email: string;
    fullName: string;
    phone?: string;
    role: string;
    organizationId?: string;
    isActive: boolean;
  }>) => api.patch(`/users/${id}`, data),

  resetPassword: (id: string, newPassword: string, mustChangePassword: boolean = true) =>
    api.post(`/users/${id}/reset-password`, { newPassword, mustChangePassword }),

  toggleActive: (id: string) => api.patch(`/users/${id}/toggle-active`),

  delete: (id: string) => api.delete(`/users/${id}`),
};

// Alerts API
// Shared filter vocabulary — GET /alerts, GET /alerts/stats and the bulk
// DELETE all accept the same params, so "delete what I am looking at" can pass
// exactly the filters the list was built with.
export interface AlertFilters {
  vehicleId?: string;
  type?: string;
  severity?: string;
  status?: 'active' | 'acknowledged' | 'resolved' | 'unread';
  isRead?: boolean;
  isResolved?: boolean;
  startDate?: string;
  endDate?: string;
  organizationId?: string;
}

export const alertsApi = {
  getAll: (params?: AlertFilters & { limit?: number; offset?: number }) =>
    api.get('/alerts', { params }),

  // Aggregates over the whole filtered set, not just the fetched page.
  getStats: (params?: AlertFilters) => api.get('/alerts/stats', { params }),

  getUnreadCount: () => api.get('/alerts/unread-count'),

  getById: (id: string) => api.get(`/alerts/${id}`),

  markAsRead: (id: string) => api.patch(`/alerts/${id}/read`),

  resolve: (id: string) => api.patch(`/alerts/${id}/resolve`),

  acknowledge: (id: string) => api.patch(`/alerts/${id}/acknowledge`),

  markAllRead: () => api.post('/alerts/mark-all-read'),

  delete: (id: string) => api.delete(`/alerts/${id}`),

  // Bulk delete. Pass the current filters to remove only the matching set;
  // `confirm: true` is required (server-side) only when no filter is given.
  // `dryRun: true` reports how many rows would go without deleting them.
  deleteBulk: (params?: AlertFilters & {
    olderThanDays?: number;
    confirm?: boolean;
    dryRun?: boolean;
  }) => api.delete('/alerts', { params }),
};

// Ambulance stations and deployment points (نقاط الإسعاف والتمركزات) —
// fixed ground positions along the inter-city corridors, distinct from the
// organizations that own vehicles.
export interface StationPoint {
  id: string;
  name: string;
  corridor: string | null;
  corridorOrder: number | null;
  kind: 'ambulance_point' | 'checkpoint' | 'staging_point';
  latitude: number;
  longitude: number;
  description: string | null;
  accuracy: 'high' | 'estimated' | null;
  isActive: boolean;
}

export const stationPointsApi = {
  getAll: (params?: { kind?: string; corridor?: string; includeInactive?: boolean }) =>
    api.get('/station-points', { params }),

  create: (data: Partial<StationPoint>) => api.post('/station-points', data),

  update: (id: string, data: Partial<StationPoint>) => api.put(`/station-points/${id}`, data),

  delete: (id: string) => api.delete(`/station-points/${id}`),
};

// Dashboard API
export const dashboardApi = {
  // Vehicles currently outside their organization's operating radius,
  // computed live from each vehicle's latest fix.
  getZoneBreaches: () => api.get('/dashboard/zone-breaches'),

  // Vehicles we have not heard from for `hours` (default: the server's
  // CONN_HIGH_AFTER_HOURS), including any that have never reported at all.
  getNoSignal: (hours?: number) =>
    api.get('/dashboard/no-signal', { params: hours ? { hours } : undefined }),

  getStats: () => api.get('/dashboard/stats'),
  
  getRecentAlerts: (limit?: number) =>
    api.get('/dashboard/recent-alerts', { params: { limit } }),
  
  getVehicleActivity: (hours?: number) =>
    api.get('/dashboard/vehicle-activity', { params: { hours } }),
  
  getHealth: () => api.get('/dashboard/health'),
};

// Reports API
// Shared shape for every report endpoint: they all accept the same range +
// scope filters, so the client passes one object through.
export interface ReportParams {
  startDate?: string;
  endDate?: string;
  vehicleId?: string;
  organizationId?: string;
}

export const reportsApi = {
  getSummary: (params?: ReportParams) => api.get('/reports/summary', { params }),

  getDailyActivity: (params?: ReportParams) => api.get('/reports/daily-activity', { params }),

  getHourlyDistribution: (params?: ReportParams) =>
    api.get('/reports/hourly-distribution', { params }),

  getVehicleTypes: (params?: ReportParams) => api.get('/reports/vehicle-types', { params }),

  getTopVehicles: (params?: ReportParams & { limit?: number }) =>
    api.get('/reports/top-vehicles', { params }),

  getAlertsSummary: (params?: ReportParams) => api.get('/reports/alerts-summary', { params }),

  // Derived trip log — trips are segmented from the position stream, since
  // the missions table has no writer.
  getTrips: (params?: ReportParams & { limit?: number; offset?: number }) =>
    api.get('/reports/trips', { params }),

  // Fuel estimate over real driven distance; rate/price are server defaults
  // unless overridden here.
  getFuel: (params?: ReportParams & { lPer100km?: number; pricePerL?: number }) =>
    api.get('/reports/fuel', { params }),

  getMaintenance: (params?: ReportParams) => api.get('/reports/maintenance', { params }),

  getPerformance: (params?: ReportParams) => api.get('/reports/performance', { params }),
};

export default api;
